<section class="page">
  <div class="page-header">
    <div>
      <h3 class="title">Generic Questions</h3>
      <p class="subtitle">
        Manage the generic questions interests must answer before the round begins.
      </p>
    </div>
  </div>

  <div class="toolbar-container">
    <div class="left-controls">
      <app-search-box tooltipText="Search By question and group" (search)="onSearch($event)"></app-search-box>
      @if (hasSelection()) {
      <button class="btn btn-icon btn-delete-bulk" (click)="deleteSelectedQuestions()"
        title="Delete Selected">
        <i class="pi pi-trash icon-lg"></i>
      </button>
      }
    </div>

    <div class="d-flex align-items-center gap-3 ms-auto">
      <button class="btn btn-primary" (click)="openCreateModal()">
        <i class="fa-solid fa-plus me-2"></i>Create New Question
      </button>
      <div class="d-flex align-items-center gap-2 border rounded px-1 header-icon-actions-wrapper">
        <button type="button" class="btn btn-icon p-1 header-icon-action-btn" (click)="resetSearch()" title="Refresh">
          <i class="pi pi-refresh icon-lg icon-gray"></i>
        </button>
        <button type="button" class="btn btn-icon p-1 tooltip-wrap header-icon-action-btn" (click)="toggleIncludeDeleted()">
          <i class="pi pi-asterisk icon-lg" [style.color]="includeDeleted() ? '#ec4899' : '#6b7280'"></i>
          <span class="tooltip-box">Include Mark Deleted</span>
        </button>
      </div>
    </div>
  </div>

  <div class="table-container shadow">
    <table class="themes-table">
      <thead>
        <tr>
          <th class="checkbox-col" width="32">
            <input type="checkbox" [checked]="allSelected()" (change)="toggleSelectAll()" />
          </th>
          <th class="sortable" (click)="onSort('question')">
            Question <span class="sort-indicator" [class.active]="currentSortField() === 'question'">{{ getSortIcon('question') }}</span>
          </th>
          <th class="sortable group-col" (click)="onSort('group')">
            Group <span class="sort-indicator" [class.active]="currentSortField() === 'group'">{{ getSortIcon('group') }}</span>
          </th>
          <th class="text-center">Action</th>
        </tr>
      </thead>
      <tbody>
        @for (q of filteredQuestions; track trackById($index, q)) {
        <tr [ngClass]="{'deleted-row': !!q.deletedAt}" [class.selected]="q.selected">
          <td class="checkbox-col">
            <input type="checkbox" [(ngModel)]="q.selected" (change)="updateSelectAllState()" />
          </td>
          <td class="fw-semibold">{{ q.question }}</td>
          <td>
            <span class="badge px-2 py-1 group-badge" [ngStyle]="getGroupBadgeStyle(q.group)">{{ q.group?.name || q.group || 'General' }}</span>
          </td>
          <td class="center action-buttons">
            <button class="btn btn-edit" (click)="openEditModal(q)" [disabled]="!!q.deletedAt">Edit</button>
          </td>
        </tr>
        } @empty {
        <tr>
          <td colspan="4" class="no-data-cell text-center text-muted py-4">No generic questions found.</td>
        </tr>
        }
      </tbody>
    </table>

    <!-- Pagination Section -->
    <div class="pagination-section">
      <div class="pagination-info">
        <span>
          Showing {{ startIndex }} to {{ endIndex }} of {{ totalCount }} Entries
        </span>
      </div>
      <div class="d-flex gap-2 align-items-center">
        @if (filteredQuestions.length !== 0) {
        <label class="page-size-label" for="pageSizeSelect">Page Size:</label>
        <select id="pageSizeSelect" [(ngModel)]="pageSize" (ngModelChange)="onPageSizeChange($event)"
          class="form-select auto-width-select">
          @for (size of pageSizes; track size) {
          <option [value]="size">{{ size }}</option>
          }
        </select>
        }
        <button class="btn btn-outline pagination-btn" [disabled]="currentPage === 1 || filteredQuestions.length === 0" (click)="previousPage()">
          &lt; Prev
        </button>
        <button class="btn btn-outline pagination-btn" [disabled]="currentPage === totalPages" (click)="nextPage()">
          Next &gt;
        </button>
      </div>
    </div>
  </div>
</section>

<!-- Create/Edit Modal Dialog -->
@if (showModal) {
<div class="modal-backdrop">
  <div class="modal-window">
    <div class="modal-header">
      <h2>{{ isEditMode ? 'Edit Generic Question' : 'Create Generic Question' }}</h2>
      <button class="btn btn-icon" (click)="closeModal()">✕</button>
    </div>
    <div class="modal-body">
      <form (ngSubmit)="onSubmit()">
        <div class="form-row">
          <div class="d-flex align-items-center justify-content-between mb-1">
            <label class="mb-0">Group *</label>
            <button type="button" class="btn btn-link p-0 text-decoration-none btn-sm toggle-group-btn" (click)="toggleNewGroupMode()">
              @if (isNewGroupMode) {
                <i class="fa-solid fa-list-ul me-1"></i>Select Existing
              } @else {
                <i class="fa-solid fa-plus me-1"></i>Create New
              }
            </button>
          </div>
          @if (isNewGroupMode) {
            <input 
              type="text" 
              class="form-control" 
              [(ngModel)]="formData.group" 
              name="group" 
              placeholder="e.g. Icebreaker, Personal, Core"
              required 
            />
          } @else {
            <select 
              class="form-select form-control" 
              [(ngModel)]="formData.group" 
              name="group" 
              required
            >
              @for (opt of groupOptions(); track opt._id) {
                <option [value]="opt._id">{{ opt.name }}</option>
              }
            </select>
          }
        </div>

        <div class="form-row mt-3">
          <label>Question Text *</label>
          <textarea 
            class="form-control" 
            [(ngModel)]="formData.question" 
            name="question" 
            placeholder="Enter question text"
            required
            rows="3"
          ></textarea>
        </div>

        <div class="modal-footer mt-4">
          <button 
            type="button" 
            class="btn btn-outline" 
            (click)="closeModal()" 
          >
            Cancel
          </button>
          <button 
            type="submit" 
            class="btn btn-primary custom-gradient-btn" 
            [disabled]="isLoading() || !formData.question.trim() || !formData.group.trim()"
          >
            @if (isLoading()) {
              <span class="spinner-border spinner-border-sm me-1" role="status"></span>
            }
            {{ isEditMode ? 'Update' : 'Create' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</div>
}
